/// <amd-module name="Agile/Directory/Redux/Favorites/FavoritesReducer" />
import { FavoriteStatus, IFavoriteData, IFavoriteState } from "Agile/Directory/DirectoryContracts";
import { DirectoryActions, DirectoryActionTypes } from "Agile/Directory/Redux/DirectoryActions";
import { FavoriteActions, FavoriteActionTypes } from "Agile/Directory/Redux/Favorites/FavoriteActions";
import produce from "immer";

/**
 * Reducer which manages the favorite state
 */
export function favoritesReducer(state: IFavoriteState, action: DirectoryActions | FavoriteActions): IFavoriteState {
    state = state || getDefaultState();

    return produce(state, draft => {
        switch (action.type) {
            case DirectoryActionTypes.InitialDataLoaded: {
                const { favorites, favoriteStatuses } = buildFavoritesMap(action.payload.favorites);
                draft.favorites = favorites;
                draft.favoriteStatuses = favoriteStatuses;
                break;
            }

            case FavoriteActionTypes.BeginAddFavorite: {
                const artifactId = action.payload;
                delete draft.favoriteErrors[artifactId];
                draft.favorites[artifactId] = {
                    artifactId: artifactId,
                    artifactName: "",
                    artifactType: "",
                    id: "",
                    isDeleted: false,
                    artifactProperties: {}
                };
                draft.favoriteStatuses[artifactId] = FavoriteStatus.Favoriting;
                break;
            }

            case FavoriteActionTypes.AddFavoriteSuccessful: {
                const favoriteData = action.payload;
                draft.favoriteStatuses[favoriteData.artifactId] = FavoriteStatus.Favorited;
                draft.favorites[favoriteData.artifactId] = favoriteData;
                break;
            }

            case FavoriteActionTypes.AddFavoriteFailed: {
                const { artifactId, error } = action.payload;
                draft.favoriteStatuses[artifactId] = FavoriteStatus.Unfavorited;
                draft.favoriteErrors[artifactId] = error;
                delete draft.favorites[artifactId];

                break;
            }

            case FavoriteActionTypes.BeginRemoveFavorite: {
                const artifactId = action.payload;
                delete draft.favoriteErrors[artifactId];
                draft.favoriteStatuses[artifactId] = FavoriteStatus.Unfavoriting;
                break;
            }

            case FavoriteActionTypes.RemoveFavoriteSuccessful: {
                const artifactId = action.payload;
                delete draft.favorites[artifactId];
                draft.favoriteStatuses[artifactId] = FavoriteStatus.Unfavorited;

                // TODO: If Favorite is deleted, remove the artifact as well
                break;
            }

            case FavoriteActionTypes.RemoveFavoriteFailed: {
                const { artifactId, error } = action.payload;
                draft.favoriteStatuses[artifactId] = FavoriteStatus.Favorited;
                draft.favoriteErrors[artifactId] = error;
                break;
            }
        }
    });
}

export function getDefaultState(): IFavoriteState {
    return {
        favorites: {},
        favoriteStatuses: {},
        favoriteErrors: {}
    };
}

function buildFavoritesMap(
    favoriteList: IFavoriteData[]
): {
    favorites: { [key: string]: IFavoriteData };
    favoriteStatuses: { [key: string]: FavoriteStatus };
} {
    const favorites = {};
    const favoriteStatuses = {};
    if (favoriteList && favoriteList.length > 0) {
        for (const favorite of favoriteList) {
            favorites[favorite.artifactId] = favorite;
            favoriteStatuses[favorite.artifactId] = FavoriteStatus.Favorited;
        }
    }

    return { favorites, favoriteStatuses };
}
